/**
 * How far down the page you are.
 *
 * A hairline across the top that fills as you scroll. On a page this long it
 * answers "how much more of this is there", which is a question people
 * genuinely have and currently have to guess at from the scrollbar.
 *
 * This is what framer-motion's `useScroll` is actually for: a value driven by
 * scroll position, written straight to a transform without a React render per
 * frame. Doing it by hand means a scroll listener, a measurement, and a
 * setState sixty times a second.
 *
 * Decorative, so it fails safe: if the motion never runs the bar simply sits at
 * zero width and nothing else on the page depends on it. It is hidden from
 * assistive technology for the same reason — the scrollbar already says this,
 * and saying it twice helps nobody.
 */

import { motion, useReducedMotion, useScroll, useSpring } from "framer-motion";
import "./ScrollProgress.css";

export function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const reduced = useReducedMotion();

  /*
   * Sprung, so the bar settles rather than snapping with every wheel notch —
   * except where less motion was asked for, in which case it tracks the scroll
   * exactly and never moves on its own.
   */
  const width = useSpring(scrollYProgress, {
    stiffness: 180,
    damping: 30,
    restDelta: 0.001,
  });

  return (
    <motion.div
      className="fc-progress"
      style={{ scaleX: reduced ? scrollYProgress : width }}
      aria-hidden="true"
    />
  );
}
